/**
 * sektion-draft — the Werte-Sektion review's LOCAL, per-user draft.
 *
 * The Werte-Sektion synthesis is HITL by design: the drafted values + statements are
 * returned in the HTTP response and persisted ONLY on confirm, so before that the whole
 * work product — the draft plus every manual edit and accepted AI proposal — lives in one
 * client signal. Any end of page-life destroyed all of it, and the cold reload fell back
 * to the »Meine Werte-Sektion entwerfen« CTA: to the user, the entire generation had
 * restarted. The data-plane 401 route-to-login (`services.ts`) makes the app itself trigger
 * that unload mid-review, which is exactly how it was hit in production.
 *
 * So the review mirrors its working section here on every mutation and restores it on
 * mount. The storage plumbing + the untrusted-boundary discipline live in `local-draft.ts`;
 * this module owns the shape and its structural guard.
 */

import type { PersonalStatements, PersonalValueEntry } from "@career/definition-v2"
import { createLocalDraftStore, type LocalDraftStore } from "./local-draft"

/** The persisted review draft — the working section plus its freshness clock. */
export interface SektionDraft {
  values: PersonalValueEntry[]
  statements: PersonalStatements
  /** When this draft was last touched (ISO-8601) — the freshness clock. */
  updatedAt: string
}

/** The statement kinds, mirrored as a runtime set for the structural guard. */
const STATEMENT_KINDS = new Set(["warum", "positionierung", "philosophie", "vision"])

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null

/**
 * One value entry. Only the three code-owned identity fields are required: `definition`
 * and `leitsatz` are optional in the domain (Basiswerte ship without them) AND may be
 * empty mid-review, because clearing a block is a legitimate edit that renders as the
 * honest »noch offen« gap. Guarding them as non-empty would silently discard a draft in
 * which the user had just emptied a field.
 */
function isValueEntry(v: unknown): v is PersonalValueEntry {
  if (!isRecord(v)) return false
  return (
    typeof v["cardId"] === "string" &&
    v["cardId"].length > 0 &&
    typeof v["tier"] === "string" &&
    typeof v["name"] === "string" &&
    (v["definition"] === undefined || typeof v["definition"] === "string") &&
    (v["leitsatz"] === undefined || typeof v["leitsatz"] === "string")
  )
}

/** One statement: a known kind plus text (empty allowed mid-review — see {@link isValueEntry}). */
function isStatement(v: unknown): boolean {
  return isRecord(v) && typeof v["kind"] === "string" && STATEMENT_KINDS.has(v["kind"]) && typeof v["text"] === "string"
}

function isStatements(v: unknown): v is PersonalStatements {
  return isRecord(v) && Array.isArray(v["statements"]) && v["statements"].every(isStatement)
}

/**
 * A total, dependency-free guard for a persisted review draft. Deliberately structural:
 * it only ensures the shape is sound enough to render and to hand back to the confirm
 * endpoint, which re-validates against the real domain validators server-side.
 */
export function isSektionDraft(v: unknown): v is SektionDraft {
  if (!isRecord(v)) return false
  return (
    Array.isArray(v["values"]) &&
    v["values"].every(isValueEntry) &&
    isStatements(v["statements"]) &&
    typeof v["updatedAt"] === "string"
  )
}

export type SektionDraftStore = LocalDraftStore<SektionDraft>

const KEY_PREFIX = "career:werte-sektion:draft:v1:"

/** A per-user review-draft store over localStorage, guarded by {@link isSektionDraft}. */
export function createSektionDraftStore(userId: string | null, explicitStorage?: Storage): SektionDraftStore {
  return createLocalDraftStore(KEY_PREFIX, userId, isSektionDraft, explicitStorage)
}
